import { useEffect, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import {
  GlobeIcon,
  MailIcon,
  MarketplaceIcon,
  MessageIcon,
  WhatsAppIcon,
} from "../../components/ui/icons";
import { Panel } from "../../components/ui/Panel";
import type {
  ChannelType,
  Channel,
  ChannelConnections,
  EbayConnections,
  User } from "../../types";
import { PIPELINE_COLORS, inputClass, slugify } from "./shared";

/**
 * How each channel type is described and validated in the UI.
 *
 * `ready` says whether an adapter exists behind the type. WhatsApp and SMS can
 * be registered before their provider is wired up -- the row is real and the
 * settings are kept -- but nothing will arrive on them, and a channel that
 * looks live while silently discarding customer messages is the worst outcome
 * available here. So the state is shown rather than hidden.
 *
 * `creatable` says whether it belongs in the Add dropdown, and only the web
 * form answers no. There is nothing here for an admin to fill in: no address to
 * register, no credential to connect. What makes a form work is a caller
 * holding an API key and posting to `/v1/tickets/webform`, so the row is seeded
 * with the database and granted in API keys. It still needs an entry in this
 * list, because that is what names the type on the channel it already has --
 * without one the seeded queue would be labelled "Email (Gmail)".
 *
 * `heading` names the group in the list and `label` names the option in the Add
 * dropdown, which is why email carries two of them. The dropdown is a choice
 * between providers and "(Gmail)" is the useful half of it; a heading over rows
 * that already show a Gmail address is answering a question nobody asked.
 *
 * `icon` is the bare glyph, not `ChannelIcon`. That component is the queue's
 * version -- glyph, tooltip and a screen-reader label, because there it stands
 * in for a word that is not on the row. Here the word is right beside it, so a
 * second one read aloud is noise and the tooltip is redundant.
 */
const CHANNEL_KINDS: {
  type: ChannelType;
  heading: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  ready: boolean;
  creatable?: boolean;
  addressLabel: string;
  addressPlaceholder: string;
  /** Mirrors the server's check, so the message lands beside the field. */
  valid: (value: string) => boolean;
}[] = [
  {
    type: "gmail",
    heading: "Email",
    icon: MailIcon,
    label: "Email (Gmail)",
    ready: true,
    addressLabel: "Channel address",
    addressPlaceholder: "orders@netxl.com",
    valid: (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim()),
  },
  {
    type: "whatsapp",
    heading: "WhatsApp",
    icon: WhatsAppIcon,
    label: "WhatsApp",
    ready: true,
    addressLabel: "WhatsApp number",
    addressPlaceholder: "+447700900123",
    valid: (value) => /^\+[1-9]\d{7,14}$/.test(value.trim()),
  },
  {
    type: "ebay",
    heading: "eBay",
    icon: MarketplaceIcon,
    label: "eBay messages",
    ready: true,
    addressLabel: "Seller username",
    addressPlaceholder: "netxl_uk",
    valid: (value) => /^[^\s@&'<>]{2,64}$/.test(value.trim()),
  },
  {
    type: "sms",
    heading: "SMS",
    icon: MessageIcon,
    label: "SMS",
    ready: false,
    addressLabel: "Sender number",
    addressPlaceholder: "+447700900123",
    valid: (value) => /^\+[1-9]\d{7,14}$/.test(value.trim()),
  },
  {
    type: "webform",
    heading: "Web form",
    icon: GlobeIcon,
    label: "Web form",
    ready: true,
    creatable: false,
    addressLabel: "Form page",
    addressPlaceholder: "https://www.netxl.com/contact-us",
    valid: (value) => value.trim().length > 0,
  },
];

function kindOf(type: ChannelType) {
  return CHANNEL_KINDS.find((entry) => entry.type === type) ?? CHANNEL_KINDS[0]!;
}

/**
 * The list, split by type, in `CHANNEL_KINDS` order.
 *
 * A flat list was one long column of rows that looked alike and behaved
 * differently: only Gmail can be connected, only WhatsApp has an out-of-hours
 * reply, and only Gmail can send Pulse's own notifications. Every row therefore
 * carried a different subset of controls in the same place, and reading it meant
 * working out which type each one was before any of it made sense. Grouping
 * answers that once per heading instead of once per row.
 *
 * Empty groups are dropped rather than shown empty. A heading over nothing reads
 * as a channel that has gone missing, and the Add dropdown is where an admin
 * finds out that a type exists -- SMS in particular would otherwise be a section
 * permanently announcing a provider nobody has built.
 */
function groupByKind(channels: Channel[]) {
  return CHANNEL_KINDS.map((kind) => ({
    kind,
    channels: channels.filter((channel) => channel.type === kind.type),
  })).filter((group) => group.channels.length > 0);
}

export function ChannelSettings() {
  const queryClient = useQueryClient();
  const channels = useQuery({
    queryKey: queryKeys.channels,
    queryFn: api.channels,
  });

  /*
   * Authorisation state, admin-only and separate from the channel list every
   * agent reads. Failing quietly matters here: on a server with no OAuth client
   * configured this 403s or errors, and the column simply does not render rather
   * than showing every channel as broken.
   */
  const connections = useQuery({
    queryKey: queryKeys.channelConnections,
    queryFn: api.channelConnections,
    retry: false,
  });

  /* The same, for eBay's seller accounts, which are a credential of their own. */
  const ebayConnections = useQuery({
    queryKey: queryKeys.ebayConnections,
    queryFn: api.ebayConnections,
    retry: false,
  });

  /*
   * The desk, for the one control that names a person: which agent a WhatsApp
   * number belongs to. Fetched here rather than in the editor so opening three
   * of them does not fetch the list three times, and so a row can render the
   * owner's *name* rather than an id it would have to look up to show.
   */
  const users = useQuery({ queryKey: queryKeys.users, queryFn: api.users });

  const [type, setType] = useState<ChannelType>("gmail");
  const [address, setAddress] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [color, setColor] = useState(PIPELINE_COLORS[0]!);
  const [addError, setAddError] = useState<string | null>(null);

  /*
   * Meta's id for the sending number, which is not the number.
   *
   * Its own field rather than part of the address, because they are two
   * different values off the same screen in Meta's dashboard and pasting the
   * wrong one is the single easiest mistake to make here -- the id is digits,
   * the number is digits, and neither looks obviously like the other.
   */
  const [phoneNumberId, setPhoneNumberId] = useState("");

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.channels });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.channelConnections,
    });
  };

  /*
   * A rename reaches further than the channel list.
   *
   * The display name is denormalised onto everything that shows it, because the
   * alternative is every ticket row joining the channels table to draw one
   * tooltip: `TicketSummary.channelName` on each row, `channel.displayName` on
   * a ticket's detail, the "send as" options the New Ticket dialog labels its
   * picker with, and the dashboard's filter list. Invalidating only
   * `["channels"]` would leave the new name in Settings and the old one on
   * every screen an agent actually looks at, until something else happened to
   * refetch them.
   *
   * The colour needs none of this today -- nothing outside this panel draws it
   * -- but it travels with the name and one sweep is easier to keep true than
   * two rules about which field moved.
   */
  const refreshAfterRename = () => {
    refresh();
    void queryClient.invalidateQueries({ queryKey: queryKeys.sendFrom });
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
    void queryClient.invalidateQueries({ queryKey: queryKeys.ticketDetailRoot });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboardFilters });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createChannel({
        type,
        address: address.trim(),
        slug: slugify(displayName),
        displayName: displayName.trim(),
        color,
        isActive: true,
        ...(type === "whatsapp"
          ? { config: { phoneNumberId: phoneNumberId.trim() } }
          : {}),
      }),
    onSuccess: () => {
      refresh();
      setAddress("");
      setDisplayName("");
      setPhoneNumberId("");
      setAddError(null);
    },
    onError: (cause: unknown) =>
      setAddError(cause instanceof Error ? cause.message : "Could not add"),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => api.deactivateChannel(id),
    onSuccess: refresh,
  });

  const activate = useMutation({
    mutationFn: (id: number) => api.activateChannel(id),
    onSuccess: refresh,
  });

  /*
   * Which address Pulse's own notification emails come from.
   *
   * A per-channel control rather than a field somewhere in general settings,
   * because the thing being chosen is one of these rows -- and the server
   * clears the flag from the others, so this behaves as one choice across the
   * list rather than as a switch per row.
   */
  const setSender = useMutation({
    mutationFn: (id: number) =>
      api.updateChannel(id, { sendsNotifications: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.channels }),
  });

  /*
   * One button, two credentials: a Gmail channel's consent and an eBay
   * seller's live in different tables behind different routes. eBay has no
   * revoke, so what the server says about finishing the job on eBay's side is
   * shown rather than swallowed.
   */
  const disconnect = useMutation({
    mutationFn: async (channel: Channel) => {
      if (channel.type !== "ebay") {
        await api.disconnectChannel(channel.id);
        return null;
      }
      return (await api.disconnectEbay(channel.id)).note;
    },
    onSuccess: (note) => {
      refresh();
      if (note) setOutcome({ kind: "connected", detail: note });
    },
  });

  /*
   * The consent flow ends at /settings?channel=..., because Google redirects a
   * browser here rather than answering the SPA. Read it once on mount and strip
   * it from the URL, so the banner does not reappear on every later render and a
   * refresh does not resurrect a stale outcome.
   */
  const [outcome, setOutcome] = useState<{
    kind: "connected" | "failed";
    detail: string;
  } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("channel");
    if (!result) return;

    if (result === "connected") {
      setOutcome({
        kind: "connected",
        detail: `${params.get("address") ?? "The channel"} is connected.`,
      });
    } else {
      setOutcome({
        kind: "failed",
        detail:
          params.get("reason") ??
          (result === "denied"
            ? "Authorisation was cancelled."
            : "That authorisation link was not valid. Try again."),
      });
    }

    // The connection state changed; the panel must not show what it cached.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.channelConnections,
    });
    window.history.replaceState({}, "", window.location.pathname);
  }, [queryClient]);

  const kind = kindOf(type);
  const slug = slugify(displayName);
  const slugTaken = (channels.data ?? []).some((entry) => entry.slug === slug);
  const addressTaken = (channels.data ?? []).some(
    (entry) => entry.address.toLowerCase() === address.trim().toLowerCase(),
  );

  /* A WhatsApp channel with no phone number id can neither receive nor reply,
     so it is refused here as well as by the server. */
  const whatsappReady = type !== "whatsapp" || /^\d+$/.test(phoneNumberId.trim());

  const canAdd =
    displayName.trim().length > 0 &&
    kind.valid(address) &&
    whatsappReady &&
    !slugTaken &&
    !addressTaken &&
    !create.isPending;

  const groups = groupByKind(channels.data ?? []);

  return (
    <Panel
      collapsible
      title="Channels"
    >
      {outcome ? (
        <p
          className={
            outcome.kind === "connected"
              ? "mb-3 rounded-md bg-emerald-50 p-2 text-xs text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
              : "mb-3 rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950 dark:text-red-300"
          }
        >
          {outcome.detail}
        </p>
      ) : null}

      <div className="space-y-4">
        {groups.map((group) => {
          const Icon = group.kind.icon;
          return (
            /*
             * `<details>` rather than state and a conditional, for the reasons
             * Panel gives: disclosure semantics, keyboard handling and
             * find-in-page for free, and the browser keeps the open state while
             * the page is mounted. Open by default -- this folds a list away
             * once you have found what you came for, and a panel that opens
             * with everything hidden would make you open three things to see
             * what used to be on the screen already.
             *
             * The group is *named*. `group-open:` compiles to `.group:open &`,
             * which matches any open `.group` ancestor -- and Panel's own
             * `<details>` is one, so an unnamed marker in here would rotate
             * when the Channels panel opened and then sit wrong for the rest of
             * the session.
             */
            <details key={group.kind.type} open className="group/type">
              <summary className="mb-1.5 cursor-pointer list-none">
                <h3 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted hover:text-content">
                  <span
                    aria-hidden
                    className="shrink-0 text-faint transition-transform group-open/type:rotate-90"
                  >
                    ▶
                  </span>
                  <Icon className="size-3.5 shrink-0" />
                  {group.kind.heading}
                  <span className="tracking-normal text-faint">
                    {group.channels.length}
                  </span>
                </h3>
              </summary>

              <div className="space-y-1.5">
                {group.channels.map((channel) => (
                  <ChannelRow
                    key={channel.id}
                    channel={channel}
                    ready={group.kind.ready}
                    connections={connections.data ?? null}
                    ebayConnections={ebayConnections.data ?? null}
                    users={users.data ?? []}
                    onDisconnect={() => disconnect.mutate(channel)}
                    disconnecting={disconnect.isPending}
                    onSetSender={() => setSender.mutate(channel.id)}
                    settingSender={setSender.isPending}
                    onDeactivate={() => deactivate.mutate(channel.id)}
                    deactivating={deactivate.isPending}
                    onActivate={() => activate.mutate(channel.id)}
                    activating={activate.isPending}
                    onSaved={refresh}
                    onRenamed={refreshAfterRename}
                  />
                ))}
              </div>
            </details>
          );
        })}

        {channels.data?.length === 0 ? (
          <p className="px-2 py-3 text-xs text-faint">No channels yet.</p>
        ) : null}
      </div>

      <div className="mt-4 border-t border-line pt-3">
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
          Add a channel
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={type}
            onChange={(event) => {
              setType(event.target.value as ChannelType);
              // The address format differs per type, so a half-typed one is
              // never still valid after a switch -- and the phone number id
              // belongs to WhatsApp alone.
              setAddress("");
              setPhoneNumberId("");
              setAddError(null);
            }}
            className={inputClass + " h-8"}
          >
            {CHANNEL_KINDS.filter((entry) => entry.creatable !== false).map(
              (entry) => (
                <option key={entry.type} value={entry.type}>
                  {entry.label}
                </option>
              ),
            )}
          </select>

          <input
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder={kind.addressPlaceholder}
            aria-label={kind.addressLabel}
            className={inputClass + " h-8 w-56"}
          />

          {type === "whatsapp" ? (
            <input
              value={phoneNumberId}
              onChange={(event) => setPhoneNumberId(event.target.value)}
              placeholder="Meta phone number id"
              aria-label="Meta phone number id"
              className={inputClass + " h-8 w-48"}
            />
          ) : null}

          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Name for this channel"
            aria-label="Display name"
            className={inputClass + " h-8 w-48"}
          />

          <div className="flex flex-wrap items-center gap-1">
            {PIPELINE_COLORS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setColor(option)}
                aria-label={`Colour ${option}`}
                aria-pressed={color === option}
                style={{ backgroundColor: option }}
                className={cn(
                  "size-5 rounded-full ring-offset-2 ring-offset-surface",
                  color === option && "ring-2 ring-content",
                )}
              />
            ))}
          </div>

          <Button onClick={() => create.mutate()} disabled={!canAdd}>
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </div>

        {/*
          Shown while typing because this is the only moment it is decided. The
          slug is taken from the name here and then left alone -- renaming the
          channel later does not move it -- so an admin who wants a particular
          handle has to get the name right now.
        */}
        {displayName.trim() ? (
          <p className="mt-1.5 text-xs text-faint">
            Slug: {slug || "…"} — how other settings refer to this channel. It
            is taken from the name now, and stays put if the channel is renamed
            later.
          </p>
        ) : null}

        {/*
          Setup this screen cannot do, stated before the button is pressed
          rather than left to be discovered when nothing arrives.
        */}
        {type === "gmail" ? (
          <p className="mt-1.5 text-xs text-muted">
            Once added, use Connect on the row to authorise this inbox: an
            admin signs in as the address itself and consents once. Until that
            happens the channel cannot sync or send, and the first sync fails
            with an authorisation error.
          </p>
        ) : type === "whatsapp" ? (
          <p className="mt-1.5 text-xs text-muted">
            The phone number id is on the API Setup screen in the Meta app
            dashboard, beside the number — it is <em>not</em> the number itself.
            Inbound messages reach Pulse through the relay on netxl-api, and
            sending needs a system-user token in the server environment; neither
            is configured from here.
          </p>
        ) : type === "ebay" ? (
          <p className="mt-1.5 text-xs text-muted">
            Once added, use Connect on the row — signing in to eBay as this
            seller, or, on this server&rsquo;s shared keyset, pasting the
            refresh token the listings and orders integration holds. Pulse then
            checks for new member messages every minute or so — eBay has no
            push — and only messages sent after connecting become tickets.
          </p>
        ) : (
          <p className="mt-1.5 text-xs text-amber-600">
            No {kind.label} provider is connected yet. The channel will be saved
            and kept, but nothing will send or receive on it until the adapter
            is configured.
          </p>
        )}

        {type === "whatsapp" &&
        phoneNumberId.trim() &&
        !/^\d+$/.test(phoneNumberId.trim()) ? (
          <p className="mt-1.5 text-xs text-amber-600">
            A phone number id is digits only. If what you have starts with a
            “+”, that is the number — the id is the other value on the same
            screen.
          </p>
        ) : null}

        {address.trim() && !kind.valid(address) ? (
          <p className="mt-1.5 text-xs text-amber-600">
            {type === "gmail"
              ? "Enter the full channel address."
              : type === "ebay"
                ? "Enter the seller's eBay username, exactly as it signs in."
                : "Enter the number in E.164 form, e.g. +447700900123."}
          </p>
        ) : null}
        {addressTaken ? (
          <p className="mt-1.5 text-xs text-amber-600">
            That address is already registered.
          </p>
        ) : null}
        {slugTaken && displayName.trim() ? (
          <p className="mt-1.5 text-xs text-amber-600">
            There is already a channel with that name.
          </p>
        ) : null}
        {addError ? (
          <p className="mt-1.5 text-xs text-red-600">{addError}</p>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * One channel, as two lines rather than one.
 *
 * The row used to be a single flex line: a name chip, a fixed-width address, a
 * queue slug, then everything else pushed to the right in one undifferentiated
 * run -- "Connected", "Disconnect", "Live", "Send notifications from here",
 * "Deactivate". Four of those are states and three are actions, and at the same
 * size and weight in the same row there was nothing to tell you which was
 * which. Anything longer than the fixed widths wrapped, so a long address moved
 * the buttons onto their own line and the run stopped lining up down the list.
 *
 * So: identity on the left, over two lines -- what the channel is called, then
 * what it actually is. State and actions on the right, also over two lines, with
 * the states as pills and the actions as buttons. The split is what makes the
 * column readable; the two rows on each side are what stop it wrapping.
 *
 * Editors open *below* the whole row rather than inside the flex, because a
 * textarea or a strip of swatches in a row of chips reflows everything around
 * it. Which one is open lives here rather than in the editors, so the button
 * and the panel it opens can sit in different places.
 */
function ChannelRow({
  channel,
  ready,
  connections,
  ebayConnections,
  users,
  onDisconnect,
  disconnecting,
  onSetSender,
  settingSender,
  onDeactivate,
  deactivating,
  onActivate,
  activating,
  onSaved,
  onRenamed,
}: {
  channel: Channel;
  /** Whether an adapter exists behind this type; from `CHANNEL_KINDS`. */
  ready: boolean;
  connections: ChannelConnections | null;
  ebayConnections: EbayConnections | null;
  /** The desk, so a WhatsApp number's owner can be picked and named. */
  users: User[];
  onDisconnect: () => void;
  disconnecting: boolean;
  onSetSender: () => void;
  settingSender: boolean;
  onDeactivate: () => void;
  deactivating: boolean;
  onActivate: () => void;
  activating: boolean;
  onSaved: () => void;
  /** Wider than `onSaved`: the display name is copied onto other payloads. */
  onRenamed: () => void;
}) {
  /*
   * One editor at a time, rather than a flag each. They open into the same slot
   * under the row, and two of them there at once would push the second one so
   * far from the button that opened it that it would read as belonging to the
   * next channel down.
   */
  const [editor, setEditor] = useState<
    "details" | "outOfHours" | "owner" | "ebayToken" | null
  >(null);

  /*
   * An eBay channel on a keyset with no RuName of Pulse's own is connected by
   * pasting a refresh token, so its Connect is a button opening a form rather
   * than a link to eBay's consent screen.
   */
  const pastesToken =
    channel.type === "ebay" && ebayConnections !== null && !ebayConnections.consentAvailable;

  const owner = users.find((user) => user.id === ownerIdOf(channel)) ?? null;

  const auth =
    channel.type === "gmail" && connections
      ? authStateOf(channel, connections)
      : channel.type === "ebay" && ebayConnections
        ? ebayAuthStateOf(channel, ebayConnections)
        : null;
  const health = healthOf(channel, ready);

  /*
   * Ask Gmail for a watch now rather than at the daily renewal. Offered only
   * while push is failing -- on a healthy channel it would do nothing anybody
   * could see. Gmail's answer replaces the status's either way, so the list is
   * refetched; a refusal is also shown under the buttons, because a tooltip on
   * a pill is not where anybody looks for the result of pressing something.
   */
  const queryClient = useQueryClient();
  const repush = useMutation({
    mutationFn: () => api.registerWatch(channel.id),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels }),
  });
  const repushError = repush.error
    ? repush.error.message
    : repush.data && !repush.data.registered
      ? (repush.data.error ?? "Gmail did not register a watch.")
      : null;

  const actions = [
    channel.type === "gmail" && channel.isActive && channel.watchError ? (
      <Button
        key="push"
        size="sm"
        variant="secondary"
        onClick={() => repush.mutate()}
        disabled={repush.isPending}
        title="Ask Gmail to push this inbox's mail to Pulse again. Reconnect first if the credential is broken."
      >
        {repush.isPending ? "Retrying push…" : "Retry push"}
      </Button>
    ) : null,

    auth?.action === "disconnect" ? (
      <Button
        key="disconnect"
        size="sm"
        variant="secondary"
        onClick={onDisconnect}
        disabled={disconnecting}
        title={
          channel.type === "ebay"
            ? "Forgets the stored eBay authorisation. eBay has no revoke, so the seller removes the app on eBay to finish."
            : "Forgets the stored credential and revokes it with Google."
        }
      >
        Disconnect
      </Button>
    ) : null,

    /*
     * Connect is an anchor, not a fetch: the consent flow is a redirect chain
     * through accounts.google.com, which cannot happen inside XHR. It borrows
     * the secondary button's classes so it does not read as a different kind of
     * control from the ones beside it.
     */
    (auth?.action === "connect" || auth?.action === "reconnect") && pastesToken ? (
      <Button
        key="connect"
        size="sm"
        variant="secondary"
        onClick={() => setEditor((open) => (open === "ebayToken" ? null : "ebayToken"))}
        aria-expanded={editor === "ebayToken"}
        title="Paste the seller's refresh token from the NetXL listings and orders integration."
      >
        {auth.action === "reconnect" ? "Reconnect" : "Connect"}
      </Button>
    ) : auth?.action === "connect" || auth?.action === "reconnect" ? (
      <a
        key="connect"
        href={
          channel.type === "ebay"
            ? `/api/channels/${channel.id}/ebay/start`
            : `/api/channels/${channel.id}/oauth/start`
        }
        title={
          channel.type === "ebay"
            ? `Sign in to eBay as ${channel.address} and let Pulse read and answer its member messages.`
            : auth.action === "reconnect"
              ? "Authorise this inbox again; the stored credential no longer works."
              : `Sign in as ${channel.address} and grant access to that inbox only.`
        }
        className={actionLinkClass}
      >
        {auth.action === "reconnect" ? "Reconnect" : "Connect"}
      </a>
    ) : null,

    /*
     * Offered on every channel including a deactivated one, because the two
     * things it edits -- what a channel is called and what colour it is -- are
     * how it is recognised on a ticket row and in the pickers that name it,
     * and neither has anything to do with whether it is currently ingesting.
     */
    <Button
      key="edit"
      size="sm"
      variant="ghost"
      onClick={() =>
        setEditor((open) => (open === "details" ? null : "details"))
      }
      aria-expanded={editor === "details"}
      title="Change the name and colour this channel is shown with."
    >
      Edit
    </Button>,

    /*
     * Only offered on a live Gmail channel, because those are the only ones
     * that can send. Shown as a state rather than a switch: there is one
     * sender, so the useful controls are "this is it" -- the pill above -- and
     * "make this it", not a box per row that can be ticked twice.
     */
    channel.type === "gmail" && channel.isActive && !channel.sendsNotifications ? (
      <Button
        key="sender"
        size="sm"
        variant="ghost"
        onClick={onSetSender}
        disabled={settingSender}
        title="Send Pulse's notification emails from this address. Best on a no-reply address, not the one customers write to."
      >
        Send notifications from here
      </Button>
    ) : null,

    channel.type === "whatsapp" && channel.isActive ? (
      <Button
        key="ooh"
        size="sm"
        variant="ghost"
        onClick={() =>
          setEditor((open) => (open === "outOfHours" ? null : "outOfHours"))
        }
        aria-expanded={editor === "outOfHours"}
        title="Sent once when a customer writes and WhatsApp's 24-hour window will close before the desk is open again."
      >
        {typeof channel.config?.outOfHoursMessage === "string" &&
        channel.config.outOfHoursMessage.trim()
          ? "Out-of-hours reply on"
          : "Set out-of-hours reply"}
      </Button>
    ) : null,

    /*
     * Only WhatsApp, and only while it is live. The rule this sets exists
     * because WhatsApp has no From line -- email has one, so an email channel
     * has nothing to decide, and a deactivated channel is not sending anything
     * for the setting to change.
     */
    channel.type === "whatsapp" && channel.isActive ? (
      <Button
        key="owner"
        size="sm"
        variant="ghost"
        onClick={() => setEditor((open) => (open === "owner" ? null : "owner"))}
        aria-expanded={editor === "owner"}
        title="Whose number this is. Their own replies go out without a name on the front; everybody else's are still signed."
      >
        {owner ? `Owned by ${owner.name}` : "Set an owner"}
      </Button>
    ) : null,

    channel.isActive ? (
      <Button
        key="deactivate"
        size="sm"
        variant="secondary"
        onClick={onDeactivate}
        disabled={deactivating}
        title="Stops ingest and hides the queue. History is kept."
      >
        Deactivate
      </Button>
    ) : (
      <Button
        key="activate"
        size="sm"
        variant="secondary"
        onClick={onActivate}
        disabled={activating}
        title="Resumes ingest and shows the queue again."
      >
        Activate
      </Button>
    ),
  ].filter(Boolean);

  return (
    <div className="rounded-md border border-line px-3 py-2 hover:bg-surface-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* The queue's colour, which is how this channel is recognised
              everywhere else in Pulse. */}
          <span
            aria-hidden
            style={{ backgroundColor: channel.color }}
            className="size-2.5 shrink-0 rounded-full"
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-content">
              {channel.displayName}
            </div>
            <div className="flex min-w-0 items-baseline gap-2 text-xs">
              <span className="truncate font-mono text-muted">
                {channel.address}
              </span>
              {/*
                The slug, as a slug. It read "/q/support" until the queues-to-
                views refactor made /q/:slug a redirect to /v/open -- from then
                on the panel was offering a URL that no longer selects this
                channel, which is worse than showing nothing, because the one
                place an admin goes to check a channel's settings is exactly
                where a wrong URL will be believed.

                It is still worth a line of its own. A web form names the email
                channel that answers for it by slug, in `config.replyChannelSlug`
                -- `ReplyService` resolves it at send time and refuses the reply
                if it matches nothing -- and it is a field on the public API's
                channel directory. So it is a handle other things hold, not
                decoration, and the tooltip says so.
              */}
              <span
                className="shrink-0 text-faint"
                title="This channel's slug: how a web form names the queue that answers for it, and what the public API reports. It does not change when the channel is renamed."
              >
                slug: {channel.slug}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {auth ? (
              <StatusPill tone={auth.tone} title={auth.title}>
                {auth.label}
              </StatusPill>
            ) : null}
            <StatusPill tone={health.tone} title={health.title}>
              {health.label}
            </StatusPill>
            {channel.sendsNotifications ? (
              <StatusPill
                tone="brand"
                title="Pulse's own notification emails go out from this address."
              >
                Sends notifications
              </StatusPill>
            ) : null}
          </div>

          {actions.length > 0 ? (
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              {actions}
            </div>
          ) : null}
          {repushError ? (
            <p className="max-w-md text-right text-xs text-red-700 dark:text-red-300">
              Push was not registered: {repushError}
            </p>
          ) : null}
        </div>
      </div>

      {editor === "details" ? (
        <ChannelDetails
          channel={channel}
          onClose={() => setEditor(null)}
          onSaved={onRenamed}
        />
      ) : null}

      {editor === "outOfHours" ? (
        <OutOfHoursReply
          channel={channel}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
        />
      ) : null}

      {editor === "ebayToken" ? (
        <EbayTokenForm
          channel={channel}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
        />
      ) : null}

      {editor === "owner" ? (
        <NumberOwner
          channel={channel}
          users={users}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
        />
      ) : null}
    </div>
  );
}

/**
 * One word about a channel, in the colour that word deserves.
 *
 * Deliberately not the `sla-*` tokens: those three are the product's way of
 * saying "you are late" and borrowing them here would put a channel's sync
 * health in the same vocabulary as a breached ticket.
 *
 * `idle` is muted text on a tinted ground, which is the pair the stylesheet
 * calls out as failing contrast -- hence `muted-on-tint` rather than `muted`.
 *
 * No status dot. The rule in this codebase is that status is never colour
 * alone, and here the word *is* the label, so a dot beside it would only be
 * repeating the pill's own tint.
 */
type Tone = "ok" | "warn" | "bad" | "idle" | "brand";

const TONES: Record<Tone, string> = {
  ok: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  warn: "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  bad: "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300",
  idle: "bg-surface-2 text-muted-on-tint",
  brand: "bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300",
};

function StatusPill({
  tone,
  title,
  children,
}: {
  tone: Tone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex max-w-full items-center truncate rounded-full px-1.5 py-0.5 text-[11px]",
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * How a Gmail channel is authorised, and what can be done about it.
 *
 * Three states worth distinguishing, because they need different actions:
 *
 *   connected            -- this inbox consented; the credential reaches it alone
 *   needs reconnecting   -- it consented once and the credential has since died
 *   not connected        -- nobody has authorised it, and nothing covers for it
 *
 * The last one is a real warning rather than an invitation: there is no
 * fallback behind it, so the channel is inert until somebody connects it.
 *
 * Data rather than markup, because the state and the button that changes it now
 * live in two different places on the row -- the pill row and the action row --
 * and a component cannot render into both.
 */
function authStateOf(
  channel: Channel,
  state: ChannelConnections,
): {
  tone: Tone;
  label: string;
  title: string;
  action: "connect" | "reconnect" | "disconnect" | null;
} {
  if (!state.oauthAvailable) {
    return {
      tone: "idle",
      label: "Not configured",
      title:
        "This server has no MAILBOX_TOKEN_KEY, so no channel can be connected and no Gmail channel can sync. Set it and restart.",
      action: null,
    };
  }

  const connection = state.connections.find(
    (entry) => entry.channelId === channel.id,
  );

  if (connection?.connected && connection.lastError) {
    return {
      tone: "bad",
      label: "Reconnect needed",
      title: connection.lastError,
      action: "reconnect",
    };
  }

  if (connection?.connected) {
    /*
     * The address is shown when it differs from the channel's, which should be
     * impossible -- the callback refuses a mismatch -- but if a channel is ever
     * renamed after being connected, the credential still belongs to the old
     * account and that is worth seeing rather than hiding.
     */
    const mismatch =
      connection.googleEmail !== null &&
      connection.googleEmail.toLowerCase() !== channel.address.toLowerCase();

    return mismatch
      ? {
          tone: "warn",
          label: `Connected as ${connection.googleEmail}`,
          title: `Authorised as ${connection.googleEmail}, which no longer matches this channel`,
          action: "disconnect",
        }
      : {
          tone: "ok",
          label: "Connected",
          title: `Authorised as ${connection.googleEmail}`,
          action: "disconnect",
        };
  }

  return {
    tone: "warn",
    label: "Not connected",
    title:
      "This channel cannot sync or send until somebody connects it. There is no fallback.",
    action: "connect",
  };
}

/** Warn this long before an eBay refresh token runs out. */
const EBAY_EXPIRY_WARNING_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * How an eBay channel's seller account is authorised.
 *
 * Gmail's three states plus a fourth, because an eBay refresh token lasts
 * about eighteen months from consent and is not extended by use. A channel
 * that is working perfectly still stops on that day, so the last month of it
 * is shown as a warning with the date, while there is time to reconnect.
 */
function ebayAuthStateOf(
  channel: Channel,
  state: EbayConnections,
): ReturnType<typeof authStateOf> {
  if (!state.available) {
    return {
      tone: "idle",
      label: "Not configured",
      title:
        "This server has no eBay keyset: EBAY_CLIENT_ID, EBAY_CLIENT_SECRET and MAILBOX_TOKEN_KEY are all needed. Set them and restart.",
      action: null,
    };
  }

  const connection = state.connections.find((entry) => entry.channelId === channel.id);

  if (!connection?.connected) {
    return {
      tone: "warn",
      label: "Not connected",
      title: "Nothing is read or sent until somebody signs in to eBay as this seller.",
      action: "connect",
    };
  }

  if (connection.lastError) {
    return {
      tone: "bad",
      label: "Reconnect needed",
      title: connection.lastError,
      action: "reconnect",
    };
  }

  const expires = connection.refreshTokenExpiresAt
    ? new Date(connection.refreshTokenExpiresAt)
    : null;

  if (expires && expires.getTime() - Date.now() < EBAY_EXPIRY_WARNING_MS) {
    return {
      tone: "warn",
      label: `Reconnect by ${expires.toLocaleDateString("en-GB")}`,
      title:
        "eBay's authorisation lasts about eighteen months and then stops. Reconnect before this date and nothing is missed.",
      action: "reconnect",
    };
  }

  /*
   * Photos go through eBay's image upload, which needs sell.inventory. A
   * channel connected before Pulse asked for it reads and replies fine but
   * refuses a photo, and pasting the same token again is the whole fix.
   */
  if (!connection.canSendImages) {
    return {
      tone: "warn",
      label: "Connected (no photos)",
      title:
        "Replies work, but photos cannot be sent: the token was stored without eBay's sell.inventory scope. " +
        "Reconnect with the same refresh token to pick it up — nothing is missed, and the token itself is unchanged.",
      action: "reconnect",
    };
  }

  const shared =
    connection.obtainedVia === "shared_token"
      ? " The token is shared with the NetXL listings and orders integration."
      : "";

  /*
   * A pasted token without eBay's identity scope cannot say whose it is, so
   * Pulse takes the channel's address as the seller's name. Worth showing,
   * because if that is wrong every message reads as the buyer's.
   */
  if (!connection.verified) {
    return {
      tone: "ok",
      label: "Connected (unverified)",
      title:
        `The token has no identity scope, so Pulse is taking ${channel.address} as the seller's eBay username without eBay confirming it. ` +
        "If it is wrong, the seller's own replies will read as the buyer's." +
        shared,
      action: "disconnect",
    };
  }

  return {
    tone: "ok",
    label: "Connected",
    title:
      `Authorised as ${connection.ebayUsername ?? channel.address}` +
      (expires ? `, until ${expires.toLocaleDateString("en-GB")}.` : ".") +
      (expires ? "" : " The token's expiry date was not given, so Pulse cannot warn before it lapses.") +
      shared,
    action: "disconnect",
  };
}

/**
 * Connect an eBay channel with a refresh token from the integration that
 * minted it -- the route when this server's keyset has no RuName of its own.
 *
 * A password field, because it is a credential that reads and sends as the
 * shop; it is sent once, encrypted by the server, and never shown again. The
 * expiry is optional and is only for the warning a month ahead: eBay tells the
 * integration that minted the token, as `refresh_token_expires_in`, and does
 * not tell anybody else.
 */
function EbayTokenForm({
  channel,
  onClose,
  onSaved,
}: {
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [token, setToken] = useState("");
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.connectEbayToken(channel.id, {
        refreshToken: token.trim(),
        refreshTokenExpiresAt: expires ? new Date(`${expires}T00:00:00Z`).toISOString() : null,
      }),
    onSuccess: () => {
      setToken("");
      setError(null);
      onClose();
      onSaved();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not connect"),
  });

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-2 p-2">
      <label
        htmlFor={`ebay-token-${channel.id}`}
        className="mb-1 block text-[11px] text-muted-on-tint"
      >
        The refresh token the NetXL listings and orders integration holds for{" "}
        {channel.address}. It must have been granted eBay&rsquo;s Message API
        (commerce.message). Pulse only mints access tokens from it; eBay does
        not change the refresh token when it does, so nothing needs writing
        back.
      </label>
      <input
        id={`ebay-token-${channel.id}`}
        type="password"
        autoComplete="off"
        value={token}
        onChange={(event) => setToken(event.target.value)}
        placeholder="v^1.1#i^1#…"
        className={inputClass + " w-full font-mono"}
      />
      <label
        htmlFor={`ebay-expires-${channel.id}`}
        className="mt-2 mb-1 block text-[11px] text-muted-on-tint"
      >
        When it expires, if the integration knows (optional — Pulse warns a
        month ahead)
      </label>
      <input
        id={`ebay-expires-${channel.id}`}
        type="date"
        value={expires}
        onChange={(event) => setExpires(event.target.value)}
        className={inputClass + " h-8"}
      />
      <div className="mt-1.5 flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || token.trim().length < 20}
        >
          {save.isPending ? "Checking with eBay…" : "Connect"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
      {error ? <p className="mt-1.5 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

/** Sync health, in the vocabulary of whichever channel type this is. */
function healthOf(
  channel: Channel,
  ready: boolean,
): { tone: Tone; label: string; title?: string } {
  if (!channel.isActive) {
    return {
      tone: "idle",
      label: "Inactive",
      title: "Ingest is stopped and the queue is hidden. History is kept.",
    };
  }

  if (!ready) {
    return {
      tone: "warn",
      label: "No provider",
      title:
        "The channel is registered and its settings are kept, but nothing will send or receive on it until an adapter is built.",
    };
  }

  /*
   * WhatsApp reports health from the same two columns, and means something a
   * little different by them: there is no sync, so `lastSyncedAt` is the last
   * payload that arrived and was stored, and `lastSyncError` is the last one
   * that could not be. "Never received" rather than "Never synced" says which.
   *
   * That distinction matters more here than on Gmail, because there is no
   * reconciliation sweep behind it -- Meta does not backfill, so a channel that
   * has gone quiet has lost whatever arrived while it was down.
   */
  if (channel.type === "whatsapp") {
    if (channel.lastSyncError) {
      return {
        tone: "bad",
        label: "Delivery failing",
        title: channel.lastSyncError,
      };
    }
    return channel.lastSyncedAt === null
      ? { tone: "idle", label: "Never received" }
      : { tone: "ok", label: "Receiving" };
  }

  /*
   * A web form has no ingest to watch. Submissions arrive as `POST
   * /v1/tickets/webform` from a caller holding an API key, so there is no sync
   * to be behind on and no credential to expire -- which is why this says what
   * the channel is for rather than borrowing Gmail's words. It used to fall
   * through to "No provider", which is the one thing it is not: the letterbox
   * is open, and whether anybody has posted through it is an API key question,
   * not a channel one.
   */
  if (channel.type === "webform") {
    return {
      tone: "idle",
      label: "Accepting submissions",
      title:
        "Submissions arrive on /v1/tickets/webform from a caller holding an API key. There is nothing to sync, so there is no health to report here.",
    };
  }

  if (channel.lastSyncError) {
    return { tone: "bad", label: "Sync failing", title: channel.lastSyncError };
  }

  if (channel.lastSyncedAt === null) {
    return { tone: "idle", label: "Never synced" };
  }

  /* eBay is always polled: its Message API has no push to be live on. */
  if (channel.type === "ebay") {
    return {
      tone: "ok",
      label: "Polling",
      title: "eBay has no push for member messages, so Pulse asks every minute or so.",
    };
  }

  /*
   * Amber rather than red: mail is still arriving, on the reconcile poll, so
   * nothing is lost -- it is late by up to one interval. But it has to say so,
   * because "Polling" below is also what a deployment with push switched off
   * shows, and that is how a refused watch went unnoticed for weeks. The API
   * also fills `watchError` for a watch that has simply run out, so the
   * sentence cannot say "Gmail refused" -- often nobody asked Gmail at all.
   */
  if (channel.watchError) {
    return {
      tone: "warn",
      label: "Push failing",
      title:
        `Push is not working: ${channel.watchError.replace(/\.?\s*$/, ".")} ` +
        "Pulse is checking this inbox on a schedule instead, so mail arrives late rather than not at all.",
    };
  }

  return channel.pushActive
    ? { tone: "ok", label: "Live", title: "Gmail is pushing to Pulse." }
    : {
        tone: "ok",
        label: "Polling",
        title: "No push watch, so Pulse is checking this inbox on a schedule.",
      };
}

/** A secondary button's clothes, for the one control that has to be a link. */
const actionLinkClass = cn(
  "inline-flex h-7 items-center justify-center rounded-md px-2.5 text-xs font-medium",
  "border border-line bg-surface text-content hover:bg-surface-2",
  "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
);

/**
 * What a channel is called and what colour it is shown in.
 *
 * The two things on a channel that are presentation and nothing else, which is
 * why they are editable at all: renaming "Support" to "Helpdesk" changes the
 * tooltip on a ticket row, the label in the New Ticket picker and the name in
 * the dashboard's filter, and changes nothing about where mail goes. The
 * address and the type are not here, and the server refuses them on a patch --
 * an address is the identity Gmail ingest matches a thread against, and editing
 * it would silently orphan every ticket already filed under it.
 *
 * The slug deliberately does not follow the name either, though it is derived
 * from the name at creation and so looks like a consequence of it. It is a
 * handle other things hold: a web form names the email channel that answers for
 * it by slug, in `config.replyChannelSlug`, and `ReplyService` resolves that at
 * send time -- so a slug that moved when somebody renamed a queue would break
 * replies on a different channel, and break them at the moment of sending
 * rather than at the moment of renaming. It is also a field on the public API's
 * channel directory. A rename is a change of label, and a label must not
 * invalidate an identifier, so the panel states what is staying put rather than
 * leaving it to be noticed.
 */
function ChannelDetails({
  channel,
  onClose,
  onSaved,
}: {
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(channel.displayName);
  const [color, setColor] = useState(channel.color);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateChannel(channel.id, {
        displayName: displayName.trim(),
        color,
      }),
    onSuccess: () => {
      setError(null);
      onClose();
      onSaved();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const named = displayName.trim().length > 0;
  const changed =
    displayName.trim() !== channel.displayName || color !== channel.color;

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-2 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          maxLength={255}
          aria-label="Channel name"
          placeholder="Name for this channel"
          className={inputClass + " h-8 w-48"}
        />

        {/* The same swatches as the Add form, so a channel cannot end up a
            colour that no other channel could have been given. */}
        <div className="flex flex-wrap items-center gap-1">
          {PIPELINE_COLORS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setColor(option)}
              aria-label={`Colour ${option}`}
              aria-pressed={color === option}
              style={{ backgroundColor: option }}
              className={cn(
                "size-5 rounded-full ring-offset-2 ring-offset-surface-2",
                color === option && "ring-2 ring-content",
              )}
            />
          ))}
        </div>

        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={!named || !changed || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDisplayName(channel.displayName);
            setColor(channel.color);
            setError(null);
            onClose();
          }}
        >
          Cancel
        </Button>
      </div>

      <p className="mt-1.5 text-[11px] text-muted-on-tint">
        Display only. This channel keeps its address and its slug ({channel.slug}
        ), so nothing that already points at it stops working.
      </p>
      {error ? (
        <p className="mt-1 text-[11px] text-red-600">{error}</p>
      ) : null}
    </div>
  );
}

/**
 * The out-of-hours notice for one WhatsApp channel.
 *
 * WhatsApp gives the desk 24 hours from a customer's message to reply freely.
 * When the desk will be shut for all of that -- a Friday evening, a weekend, a
 * bank holiday -- Pulse sends this once, so the customer knows to write again
 * rather than waiting on an answer that cannot come.
 *
 * Empty means send nothing, and that is the default: a channel does not start
 * messaging customers automatically because somebody registered it.
 *
 * Collapsed behind a button because it is set once and then not thought about,
 * and a textarea per channel would dominate a list whose job is to show at a
 * glance which channels are healthy.
 */
function OutOfHoursReply({
  channel,
  onClose,
  onSaved,
}: {
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const configured =
    typeof channel.config?.outOfHoursMessage === "string"
      ? channel.config.outOfHoursMessage
      : "";

  const [text, setText] = useState(configured);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateChannel(channel.id, {
        /*
         * `config` is replaced wholesale rather than merged, so the phone
         * number id has to be carried through. Dropping it would leave a
         * channel that can neither receive nor reply -- which the server now
         * refuses outright, but sending it is the correct thing rather than
         * relying on being told off.
         */
        config: { ...(channel.config ?? {}), outOfHoursMessage: text.trim() },
      }),
    onSuccess: () => {
      setError(null);
      onClose();
      onSaved();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-2 p-2">
      <label
        htmlFor={`ooh-${channel.id}`}
        className="mb-1 block text-[11px] text-muted-on-tint"
      >
        Sent once when a customer writes and the desk will not be open again
        before WhatsApp&rsquo;s 24-hour window closes. Leave empty to send
        nothing.
      </label>
      <textarea
        id={`ooh-${channel.id}`}
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={3}
        maxLength={4096}
        placeholder={DEFAULT_OUT_OF_HOURS_SUGGESTION}
        className={inputClass + " w-full"}
      />
      <div className="mt-1.5 flex items-center gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setText(configured);
            onClose();
            setError(null);
          }}
        >
          Cancel
        </Button>
        {!text.trim() && configured ? (
          <span className="text-[11px] text-amber-600">
            Saving empty turns the reply off.
          </span>
        ) : null}
        {error ? <span className="text-[11px] text-red-600">{error}</span> : null}
      </div>
    </div>
  );
}

/**
 * Whose number this is, or nobody's.
 *
 * Read defensively because `config` is a loose bag of per-type settings on the
 * way through the SPA -- the server is where its shape is decided -- and a
 * value that is not a number means the same as an absent one: nobody owns this
 * channel, so every reply is signed.
 */
function ownerIdOf(channel: Channel): number | null {
  const value = channel.config?.ownerUserId;
  return typeof value === "number" ? value : null;
}

/**
 * Which agent a WhatsApp number belongs to.
 *
 * WhatsApp gives the desk no From line, so the sender's name is written into
 * the message itself: a reply goes out as "*Ethan Healy*: Hi Felix". On a
 * shared number that is the only way a customer can tell three agents apart.
 * On a salesperson's direct line it is noise at best -- the customer knows
 * exactly who they are writing to -- and at worst it announces a helpdesk
 * behind what was meant to read as one person.
 *
 * So this names the owner rather than switching the signature off, and the rule
 * follows from the name: the owner's own replies go out unsigned, anybody
 * else's are signed as before. A colleague covering for them is precisely the
 * moment a customer needs telling that somebody else has picked this up, and a
 * plain on/off switch could not say that.
 *
 * Nobody is the default and the first option, because a number without a stated
 * owner is a shared one -- which is what every channel is until an admin says
 * otherwise.
 */
function NumberOwner({
  channel,
  users,
  onClose,
  onSaved,
}: {
  channel: Channel;
  users: User[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const configured = ownerIdOf(channel);
  const [ownerUserId, setOwnerUserId] = useState<number | null>(configured);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.updateChannel(channel.id, {
        /* Merged by hand, for the reason the out-of-hours editor gives: the
           server replaces `config` wholesale, and dropping the phone number id
           would leave a channel that can neither receive nor reply. */
        config: { ...(channel.config ?? {}), ownerUserId },
      }),
    onSuccess: () => {
      setError(null);
      onClose();
      onSaved();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const owner = users.find((user) => user.id === ownerUserId) ?? null;

  return (
    <div className="mt-2 rounded-md border border-line bg-surface-2 p-2">
      <label
        htmlFor={`owner-${channel.id}`}
        className="mb-1 block text-[11px] text-muted-on-tint"
      >
        Whose number this is. Their replies go out with no name on the front;
        a reply from anybody else is still signed, so the customer can see
        somebody has picked it up for them.
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <select
          id={`owner-${channel.id}`}
          value={ownerUserId ?? ""}
          onChange={(event) =>
            setOwnerUserId(event.target.value ? Number(event.target.value) : null)
          }
          className={inputClass + " h-8 w-56"}
        >
          <option value="">Nobody — sign every reply</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </select>

        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={ownerUserId === configured || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOwnerUserId(configured);
            setError(null);
            onClose();
          }}
        >
          Cancel
        </Button>
      </div>

      {/*
        Said as a sentence with the actual name in it, because the consequence
        is invisible until a customer receives one: the box says who, and this
        says what will now be sent.
      */}
      <p className="mt-1.5 text-[11px] text-muted-on-tint">
        {owner
          ? `${owner.name} replies as themselves. Everybody else's replies start with their own name, as they do now.`
          : "Every reply starts with the name of whoever sent it."}
      </p>
      {error ? (
        <p className="mt-1 text-[11px] text-red-600">{error}</p>
      ) : null}
    </div>
  );
}

/**
 * Offered as a placeholder rather than prefilled, so an empty box stays a
 * deliberate "off" instead of something an admin has to clear.
 */
const DEFAULT_OUT_OF_HOURS_SUGGESTION =
  "Thanks for your message. WhatsApp only allows us 24 hours to reply, and we " +
  "are now closed for longer than that -- so we will not be able to answer " +
  "this one. Please send it again when we are next open and we will pick it " +
  "up straight away.";
